import { useEffect, useRef } from "react";
import { HistoryIcon, LoaderCircleIcon } from "lucide-react";
import { createFileRoute } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { m } from "#/paraglide/messages";
import { msg } from "#/utils/i18n";
import { formatDateTime } from "#/utils/format";
import { useMarket } from "#/contexts/MarketContext";
import {
  useSearchEntryCloses,
  useGetEntryCloseStatus,
  useCreateEntryClose,
} from "#/lib/api/order-day";
import { useRequiredTenantId } from "#/lib/api/common";
import { OrderEntryCloseSearchSchema } from "#/lib/schemas/order-entry-close-search";
import { Card } from "#/components/atoms/card";
import { Alert, AlertDescription, AlertTitle } from "#/components/atoms/alert";
import { PageContainer } from "#/components/atoms/page-container";
import { PageHeader } from "#/components/molecules/page-header";
import { EntryCloseTable } from "#/components/organisms/order-day/entry-close-table";
import { EntryCloseForm } from "#/components/organisms/order-day/entry-close-form";
import type { EntryCloseRow } from "#/components/organisms/order-day/types";

export const Route = createFileRoute("/$tenantCode/$market/order-entry-closes")(
  {
    beforeLoad: () => ({
      getTitle: () => m["resources.orderDayEntryClose.name"](),
    }),
    validateSearch: OrderEntryCloseSearchSchema,
    component: EntryClosesRoute,
  },
);

function EntryClosesRoute() {
  const market = useMarket();
  const tenantId = useRequiredTenantId();
  const queryClient = useQueryClient();
  const { page, perPage, sort, filters } = Route.useSearch();

  const { data, isLoading } = useSearchEntryCloses({
    filters,
    sort,
    page,
    limit: perPage,
    market,
  });

  const { data: statusData } = useGetEntryCloseStatus(market);
  const { mutateAsync, isPending } = useCreateEntryClose();

  const isRunning = statusData?.isRunning === true;
  const launchedAt = statusData?.launchedAt ?? undefined;
  const prevIsRunningRef = useRef<boolean>(false);

  useEffect(() => {
    if (prevIsRunningRef.current && !isRunning) {
      void queryClient.invalidateQueries({
        queryKey: ["order-day-entry-closes-search", tenantId],
      });
    }
    prevIsRunningRef.current = isRunning;
  }, [isRunning, queryClient, tenantId]);

  const handleSubmit = async (date: string, comment?: string) => {
    if (market === undefined || market === "") return;
    await mutateAsync({ date, market, comment });
  };

  const entryCloses: EntryCloseRow[] = data?.data ?? [];
  const pageCount = data?.meta?.totalPages ?? 0;

  return (
    <PageContainer className="space-y-4">
      <PageHeader
        title={msg(m["resources.orderDayEntryClose.name"])}
        icon={HistoryIcon}
      />

      {isRunning && (
        <Alert>
          <LoaderCircleIcon className="size-4 animate-spin" />
          <AlertTitle>
            {msg(m["resources.orderDayEntryClose.status.running"])}
          </AlertTitle>
          {launchedAt !== undefined && (
            <AlertDescription>
              {msg(m["resources.orderDayEntryClose.status.launchedAt"], {
                time: formatDateTime(launchedAt),
              })}
            </AlertDescription>
          )}
        </Alert>
      )}

      {!isRunning && (
        <Card className="p-4">
          <EntryCloseForm onSubmit={handleSubmit} isPending={isPending} />
        </Card>
      )}

      <Card className="p-4">
        <EntryCloseTable
          data={entryCloses}
          pageCount={pageCount}
          isLoading={isLoading}
        />
      </Card>
    </PageContainer>
  );
}
